import { PolarityValue } from '@shared/models'
import { BrandRules, BrandTerms, DataSourceType, ExpandedRowsState, RowType } from '../types'

export const CONSTANTS = {
  SOCIAL_MEDIA_DS_ID: 1,
  PRESS_DS_ID: 2,
  MAX_RULES_PER_POLARITY: 15,
}

export const DEFAULT_NEUTRAL_RULES = [
  'For mencionada em postagem que não contenha uma carga emocional forte, focando em informação, contexto ou situações onde a responsabilidade é compartilhada ou não diretamente atribuída à ela',
  'For mencionada em postagem com variações de um contexto neutro',
]

export function parseRules(rulesText: string): string[] {
  if (!rulesText || rulesText.trim() === '') return []

  return rulesText
    .split(';')
    .map((rule) => rule.trim())
    .filter((rule) => rule.length > 0)
}

export function getRulesCount(rulesText: string): number {
  return parseRules(rulesText).length
}

export function getCurrentRules(rules: BrandRules, activeTab: DataSourceType) {
  return activeTab === 'social' ? rules.social : rules.press
}

export function getPolarityIcon(polarityType: string): string {
  switch (polarityType) {
    case 'POSITIVO':
      return PolarityValue.POSITIVE
    case 'NEGATIVO':
      return PolarityValue.NEGATIVE
    case 'FAVORÁVEL':
      return PolarityValue.FAVORABLE
    case 'DESFAVORÁVEL':
      return PolarityValue.UNFAVORABLE
    case 'NEUTRO':
      return PolarityValue.NEUTRAL
    default:
      return PolarityValue.NEUTRAL
  }
}

export function getSpecificRuleNumber(polarityType: string, ruleIndex: number): number {
  const baseNumbers = {
    POSITIVO: 1,
    NEGATIVO: 16,
    FAVORÁVEL: 31,
    DESFAVORÁVEL: 46,
    NEUTRO: 61,
  }

  const baseNumber = baseNumbers[polarityType as keyof typeof baseNumbers] || 1
  return baseNumber + ruleIndex
}

export function hasValidSpecificRulesForPolarity(
  rules: BrandRules,
  dataSource: DataSourceType,
  field: string,
): boolean {
  const rulesText = rules[dataSource][field as keyof (typeof rules)[typeof dataSource]]
  const rulesCount = parseRules(rulesText).length
  return rulesCount > 0 && rulesCount <= CONSTANTS.MAX_RULES_PER_POLARITY
}

export function isPromptConfigurationComplete(rules: BrandRules, excludedDataSources: number[] = []): boolean {
  const isSocialExcluded = excludedDataSources.includes(CONSTANTS.SOCIAL_MEDIA_DS_ID)
  const isPressExcluded = excludedDataSources.includes(CONSTANTS.PRESS_DS_ID)

  if (isSocialExcluded && isPressExcluded) {
    return false
  }

  const checkDataSource = (dataSource: DataSourceType) => {
    return [
      hasValidSpecificRulesForPolarity(rules, dataSource, 'positiveSpecific'),
      hasValidSpecificRulesForPolarity(rules, dataSource, 'negativeSpecific'),
      hasValidSpecificRulesForPolarity(rules, dataSource, 'favorableSpecific'),
      hasValidSpecificRulesForPolarity(rules, dataSource, 'unfavorableSpecific'),
    ].every(Boolean)
  }

  const socialComplete = isSocialExcluded ? true : checkDataSource('social')
  const pressComplete = isPressExcluded ? true : checkDataSource('press')

  return socialComplete && pressComplete
}

export function getRowHeight(expandedRows: ExpandedRowsState, rowType: RowType): string {
  return expandedRows[rowType] ? 'auto' : '60px'
}

export function getTermsForPolarity(polarity: string, activeTab: DataSourceType, termsState: BrandTerms) {
  const isActiveTabSocial = activeTab === 'social'

  if (isActiveTabSocial) {
    const publishersTags =
      polarity === 'POSITIVO' ? termsState.socialPositivePublishersTags
      : polarity === 'NEGATIVO' ? termsState.socialNegativePublishersTags
      : polarity === 'FAVORÁVEL' ? termsState.socialFavorablePublishersTags
      : termsState.socialUnfavorablePublishersTags

    const proprietaryTags =
      polarity === 'POSITIVO' ? termsState.socialPositiveProprietaryTags
      : polarity === 'NEGATIVO' ? termsState.socialNegativeProprietaryTags
      : polarity === 'FAVORÁVEL' ? termsState.socialFavorableProprietaryTags
      : termsState.socialUnfavorableProprietaryTags

    return { publishersTags, proprietaryTags, channelsTags: [] }
  }

  const channelsTags =
    polarity === 'POSITIVO' ? termsState.pressPositiveChannelsTags
    : polarity === 'NEGATIVO' ? termsState.pressNegativeChannelsTags
    : polarity === 'FAVORÁVEL' ? termsState.pressFavorableChannelsTags
    : termsState.pressUnfavorableChannelsTags

  const proprietaryTags =
    polarity === 'POSITIVO' ? termsState.pressPositiveProprietaryTags
    : polarity === 'NEGATIVO' ? termsState.pressNegativeProprietaryTags
    : polarity === 'FAVORÁVEL' ? termsState.pressFavorableProprietaryTags
    : termsState.pressUnfavorableProprietaryTags

  return { channelsTags, proprietaryTags, publishersTags: [] }
}
